import './Style.css';

import type { SelectableItem } from '@physitrack/championhealth-types';

import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssessmentValues } from '../../types/AssessmentPageItem';

import { Alert } from '../../../shared/components/alert/Alert';
import ModalTrigger from '../../../shared/modal/ModalTrigger';
import { ValidationContext } from '../../validation/ValidationContext';
import { CheckboxRadioItem } from './CheckboxRadioItem';

type CheckboxRadioProps = {
  assessmentValues: AssessmentValues;
  options: SelectableItem[];
  includeOnly?: SelectableItem[];
  width: string;
  skippable?: boolean;
  fadeIn?: string;
  secondaryQ?: boolean;
  labelId?: string;
  onSetValue: (payload: { id: string; value: unknown }) => void;
  questionid: string;
  questionTitle: string;
  modalText?: string;
  onOpenModal?: () => void;
  suffix?: string;
};

export function CheckboxRadio({
  assessmentValues,
  options,
  includeOnly,
  width,
  skippable,
  fadeIn,
  secondaryQ,
  labelId,
  onSetValue,
  questionid,
  questionTitle,
  modalText,
  onOpenModal,
  suffix,
}: CheckboxRadioProps) {
  const { t } = useTranslation();
  const [radioOption, setRadioOption] = useState<any>(null);
  const [isError, setIsError] = useState(false);
  const { registerValidation } = useContext(ValidationContext);

  useEffect(() => {
    registerValidation(() => {
      if (!skippable) {
        const valid = radioOption?.id;
        setIsError(!valid);
        return valid;
      }
      return true;
    });

    if (radioOption?.id) {
      setIsError(false);
    }
  }, [radioOption]);

  function addRadioItemHandler(item: any) {
    if (item?.id === radioOption?.id) {
      // already selected
      item = null;
    }
    setRadioOption(item);
    saveValue(item);
  }

  function saveValue(item) {
    if (item) delete item?.add;
    if (questionid === 'financial_position') {
      onSetValue({ id: questionid, value: item ? [item] : null });
    } else {
      onSetValue({ id: questionid, value: item });
    }
  }

  const currentFieldValue = assessmentValues?.[questionid];

  useEffect(() => {
    if (currentFieldValue && !radioOption?.id) {
      setRadioOption(() =>
        Array.isArray(currentFieldValue) ? currentFieldValue[0] : currentFieldValue
      );

      if (questionid === 'worst_area_of_pain') {
        onSetValue({
          id: questionid,
          value: currentFieldValue,
        });
      }
    }
  }, [currentFieldValue, radioOption]);

  useEffect(() => {
    if (
      (questionid === 'region' || questionid === 'sub_dept') &&
      options &&
      radioOption &&
      !options.some((el) => el.title === radioOption.title)
    ) {
      setRadioOption(null);
    }
  }, [options]);

  const [blockOpacity, setBlockOpacity] = useState(0);

  useEffect(() => {
    setTimeout(function () {
      setBlockOpacity(1);
    }, 100);
  }, []);

  return (
    <div
      className={`question_block${width}${skippable ? '' : ' req'}`}
      style={{ opacity: fadeIn ? blockOpacity : '1' }}
    >
      <div className="answer_block_container">
        <fieldset
          id={questionid}
          className="answer_block fs-exclude"
          data-input-type="checkbox_radio"
          aria-labelledby={`${labelId} ${questionid}-legend`}
        >
          <legend
            className={`${suffix ? 'srOnly' : 'assessment_question_title'}`}
            id={`${questionid}-legend`}
          >
            {isError && <span className="srOnly">{t('assessment.error.inline.info')}</span>}
            {questionTitle || suffix}
          </legend>
          {suffix && (
            <span id={`${questionid}-suffix`} className="titleSuffix">
              {suffix}
            </span>
          )}

          {modalText && (
            <ModalTrigger
              modalText={modalText}
              onOpenModal={onOpenModal}
              question={questionid}
              questionTitle={questionTitle}
            />
          )}

          <div className="checkbox active checkbox_wrapper" id={`${questionid}-checkbox-wrapper`}>
            <div className="checkbox_container">
              {options.map((option) => {
                const isIncluded =
                  !includeOnly || includeOnly.some((item) => item?.id === option?.id);
                if (!isIncluded) return null;

                const isChecked =
                  radioOption?.id === option?.id?.toString() ||
                  (secondaryQ && radioOption?.id === `${option?.id}_sq`);

                return (
                  <CheckboxRadioItem
                    key={option?.id}
                    name={questionid}
                    item={option}
                    onAddRadioItem={addRadioItemHandler}
                    checked={isChecked}
                    secondaryQ={secondaryQ}
                  />
                );
              })}
            </div>
          </div>
          <Alert
            id={`${questionid}-error`}
            isVisible={isError}
            message={t('assessment.error.inline.info.many')}
            type="error"
            isRoleAlert
          />
        </fieldset>
      </div>
    </div>
  );
}
